import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const CancellationPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page" data-testid="cancellation-policy-page">
      {/* Block this page from search engines */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Cancellation & Refund Policy - Amatya</title>
      </Helmet>
      
      <div className="legal-container">
        <h1 className="legal-title">Cancellation, Rescheduling & Refund Policy</h1>
        <p className="legal-update-date">Last Updated: 5th March 2026</p>

        <p className="legal-intro">
          At AMATYA, we are committed to providing authentic spiritual services including Puja rituals, Chadhava Seva, and Astrology Consultations with sincerity, devotion, and professionalism. Since most services offered on the platform involve customized spiritual preparations, priest scheduling, temple coordination, astrologer time allocation, and ritual arrangements, cancellations and refunds are handled carefully and only under limited circumstances. Our goal is always to resolve concerns through rescheduling, service correction, or alternative arrangements, wherever possible.
        </p>

        <div className="legal-contact-box">
          <p>For any questions regarding cancellations, rescheduling, or refunds, users may contact:</p>
          <p><strong>Email:</strong> care@amatya.co.in</p>
          <p><strong>Phone:</strong> +91-9711767167</p>
          <p><em>All requests are subject to internal review by the AMATYA Support Team.</em></p>
        </div>

        <section className="legal-section">
          <h2>1. General Policy Principles</h2>
          <ul>
            <li>1.1 Most services offered by AMATYA involve personalized spiritual arrangements, including Sankalp details, temple coordination, astrologer scheduling, and ritual preparations.</li>
            <li>1.2 Once a booking is confirmed and payment has been successfully processed, the service is considered scheduled and reserved.</li>
            <li>1.3 In most situations where a user is unable to proceed with a booked service, rescheduling or service credit may be offered instead of refunds.</li>
            <li>1.4 Refunds are considered only in limited circumstances where AMATYA is unable to deliver the service due to operational or technical reasons.</li>
            <li>1.5 AMATYA reserves the right to review each request individually before approving any cancellation or refund.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>2. Eligible Situations for Refund Consideration</h2>
          <p>Refunds may be considered only in the following circumstances:</p>
          
          <h3>2.1 Service Non-Delivery</h3>
          <p>If AMATYA is unable to perform the booked Puja, Chadhava, or Astrology service due to operational reasons and the user does not wish to reschedule.</p>
          
          <h3>2.2 Major Technical Issues</h3>
          <p>If a scheduled astrology consultation cannot be completed due to technical issues originating from the AMATYA platform or astrologer side, and the session cannot reasonably be rescheduled.</p>
          
          <h3>2.3 Duplicate Payment</h3>
          <p>If a user is charged more than once for the same service due to a payment gateway error or duplicate transaction, the excess payment will be refunded after verification.</p>
          
          <h3>2.4 Billing or System Error</h3>
          <p>If a payment is processed incorrectly due to a system error or incorrect billing, AMATYA will review the transaction and process a correction or refund if required.</p>
        </section>

        <section className="legal-section">
          <h2>3. Resolution-First Approach</h2>
          <p>Before considering a refund, AMATYA may offer one of the following resolutions:</p>
          <ul>
            <li>Rescheduling the Puja or Chadhava to another suitable date</li>
            <li>Assigning another astrologer for consultation</li>
            <li>Providing service credit for future bookings</li>
            <li>Re-performing the ritual where operationally feasible</li>
          </ul>
          <p>Refunds will generally be processed only if the above options are not possible.</p>
        </section>

        <section className="legal-section">
          <h2>4. Puja and Chadhava Services</h2>
          <ul>
            <li>4.1 Puja and Chadhava services involve sacred rituals performed by priests and temple authorities based on personalized Sankalp details.</li>
            <li>4.2 Once preparations for a ritual have commenced, the booking cannot be cancelled.</li>
            <li>4.3 Requests for modifications such as:
              <ul>
                <li>Puja date change</li>
                <li>Sankalp corrections</li>
                <li>Participant detail updates</li>
              </ul>
              may be accommodated only if operationally feasible.
            </li>
            <li>4.4 Once a ritual has been performed as per the booked service, the service is considered fulfilled.</li>
            <li>4.5 In such cases, refunds will not be issued based on personal preferences or expectations regarding spiritual outcomes.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Astrology Consultation Services</h2>
          <ul>
            <li>5.1 Astrology consultations offered on AMATYA are guidance-based spiritual services.</li>
            <li>5.2 Refunds will not be issued due to:
              <ul>
                <li>disagreement with astrological insights</li>
                <li>dissatisfaction with predictions</li>
                <li>personal expectations from the consultation.</li>
              </ul>
            </li>
            <li>5.3 If a consultation is interrupted due to technical issues from the astrologer side, AMATYA will arrange:
              <ul>
                <li>continuation of the session, or</li>
                <li>rescheduling with the same or another astrologer.</li>
              </ul>
            </li>
            <li>5.4 Once a consultation has been substantially completed, the service will be considered delivered.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Cancellation Requests</h2>
          <ul>
            <li>6.1 If a user wishes to request cancellation before the service begins, the request must be submitted at least 72 hours prior to the scheduled service time.</li>
            <li>6.2 In such cases, AMATYA may offer:
              <ul>
                <li>rescheduling of the service, or</li>
                <li>service credit for future bookings.</li>
              </ul>
            </li>
            <li>6.3 Refunds for cancellations will generally not be processed once service preparations have commenced.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Refund Request Timeline</h2>
          <ul>
            <li>7.1 If a refund is eligible under this policy, the request must be submitted at least 72 hours prior to the scheduled service date or issue occurrence.</li>
            <li>7.2 Requests submitted beyond this period will not be considered.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>8. Refund Processing</h2>
          <ul>
            <li>8.1 Approved refunds will be processed within 7–10 business days from the date of approval.</li>
            <li>8.2 Refunds will be issued to the original payment method used during the transaction, unless otherwise required by payment regulations.</li>
            <li>8.3 Users will receive confirmation once the refund has been processed.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Non-Refundable Situations</h2>
          <p>Refunds will not be issued in the following circumstances:</p>
          <ul>
            <li>Change of mind after booking</li>
            <li>Incorrect information submitted by the user</li>
            <li>Personal circumstances preventing participation</li>
            <li>Dissatisfaction with astrological interpretations</li>
            <li>Rituals already performed</li>
            <li>Missed consultations</li>
            <li>Requests made after the reporting timeline</li>
            <li>Delays caused by external logistics partners</li>
            <li>Situations beyond reasonable operational control</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>10. Payment Disputes and Chargebacks</h2>
          <ul>
            <li>10.1 Users are encouraged to contact AMATYA support directly to resolve any payment concerns.</li>
            <li>10.2 By completing a payment on the platform, the user confirms that they have read and agreed to this Cancellation, Rescheduling & Refund Policy.</li>
            <li>10.3 Users agree not to initiate payment disputes or chargebacks with banks or payment gateways without first contacting AMATYA support.</li>
            <li>10.4 In case a chargeback is initiated after the service has been delivered or scheduled, AMATYA may submit all relevant booking records, communication logs, and service documentation to the payment processor.</li>
            <li>10.5 If a payment dispute is found to be fraudulent or abusive, AMATYA reserves the right to:
              <ul>
                <li>restrict or suspend the user account</li>
                <li>refuse future bookings</li>
                <li>recover administrative costs where applicable.</li>
              </ul>
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>11. Spiritual Services Disclaimer</h2>
          <p>
            AMATYA provides spiritual services including Puja rituals, Chadhava offerings, astrology consultations, and related spiritual guidance for purposes of religious devotion and personal reflection. All astrology readings and spiritual interpretations are based on traditional beliefs, scriptures, and practitioner experience. AMATYA does not guarantee specific outcomes, including but not limited to:
          </p>
          <ul>
            <li>financial success</li>
            <li>relationship outcomes</li>
            <li>career changes</li>
            <li>health improvements</li>
            <li>resolution of personal issues.</li>
          </ul>
          <p>
            Users acknowledge that astrology and spiritual services are interpretative and faith-based practices, and any decisions made based on such guidance remain the personal responsibility of the user.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. No Professional Advice</h2>
          <p>
            Astrology consultations and spiritual guidance provided on AMATYA do not constitute legal, medical, financial, psychological, or professional advice. Users are advised to consult qualified professionals for matters related to:
          </p>
          <ul>
            <li>health or medical issues</li>
            <li>legal matters</li>
            <li>financial or investment decisions</li>
            <li>mental health concerns</li>
            <li>professional career decisions.</li>
          </ul>
          <p>
            AMATYA shall not be responsible for actions taken by users based on the guidance received through the platform.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted under applicable law, AMATYA, its affiliates, employees, astrologers, priests, and partners shall not be liable for any indirect, incidental, or consequential damages arising from the use of the platform or services. In any circumstance where liability is legally established, the maximum liability of AMATYA shall not exceed the amount paid by the user for the specific service in question.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Force Majeure</h2>
          <p>In rare situations beyond the control of AMATYA, including but not limited to:</p>
          <ul>
            <li>natural disasters</li>
            <li>temple closures</li>
            <li>government restrictions</li>
            <li>technical outages or unforeseen operational disruptions</li>
          </ul>
          <p>AMATYA may reschedule the service or offer service credit or refund at its discretion.</p>
        </section>

        <section className="legal-section">
          <h2>15. Final Decision Authority</h2>
          <p>
            All cancellation, rescheduling, and refund requests are subject to final review by the AMATYA Support Team. AMATYA reserves the right to decline refund requests where misuse of this policy or abnormal booking activity is suspected.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Acceptance of Policy</h2>
          <p>
            By booking any service on the AMATYA platform, users confirm that they have read, understood, and agreed to this Cancellation, Rescheduling & Refund Policy.
          </p>
        </section>

        <div className="legal-footer-note">
          <p><strong>ANS LEGAL AND BUSINESS SERVICES LLP</strong></p>
          <p>care@amatya.co.in</p>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicyPage;
